import { Controller } from "@hotwired/stimulus"
import {
  beginSkeletonWait,
  endSkeletonWait,
  waitTarget,
} from "./skeleton_wait_support"
import {
  isPrefetchRequest,
  SORT_LINK_SELECTOR,
  SKELETON_SHOWN_EVENT,
  turboFrameHeader,
} from "./table_frame_support"

const SKELETON_SELECTOR = [
  ".aqui-skeleton",
  "[data-educator-home-skeleton]",
  "[data-educator-home-page-course-monitoring-skeleton-rows]",
  "[data-educator-home-student-activity-log-skeleton-rows]",
  "[data-educator-home-page-course-monitoring-students-skeleton]",
].join(", ")
// ponytail: safety clear if Turbo never resolves (hung request)
const WAITING_SAFETY_MS = 90_000

export default class extends Controller {
  connect() {
    this.waitingForFrameId = null
    this.boundBeforeFetch = this.onBeforeFetch.bind(this)
    this.boundTurboClick = this.onTurboClick.bind(this)
    this.boundFrameLoad = this.onFrameLoad.bind(this)
    this.boundFetchError = this.onFetchError.bind(this)
    this.boundFrameMissing = this.onFrameMissing.bind(this)
    this.boundSkeletonShown = () => this.applyWait()

    this.beforeFetchTarget =
      this.element.closest("[data-educator-home-skeleton-wait-scope]") ?? this.element
    this.beforeFetchTarget.addEventListener("turbo:before-fetch-request", this.boundBeforeFetch)
    this.element.addEventListener("turbo:click", this.boundTurboClick)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
    this.element.addEventListener("turbo:fetch-request-error", this.boundFetchError)
    this.element.addEventListener("turbo:frame-missing", this.boundFrameMissing)
    this.element.addEventListener(SKELETON_SHOWN_EVENT, this.boundSkeletonShown)

    if (this.hasSkeleton()) this.applyWait(this.element.id)
  }

  disconnect() {
    this.clearWait()

    this.beforeFetchTarget?.removeEventListener("turbo:before-fetch-request", this.boundBeforeFetch)
    this.element.removeEventListener("turbo:click", this.boundTurboClick)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    this.element.removeEventListener("turbo:fetch-request-error", this.boundFetchError)
    this.element.removeEventListener("turbo:frame-missing", this.boundFrameMissing)
    this.element.removeEventListener(SKELETON_SHOWN_EVENT, this.boundSkeletonShown)
  }

  onBeforeFetch(event) {
    if (!this.isFetchForThisFrame(event)) return
    if (isPrefetchRequest(event.detail?.fetchOptions?.headers)) return

    this.applyWait(
      turboFrameHeader(event.detail?.fetchOptions?.headers) || this.element.id,
    )
  }

  onTurboClick(event) {
    const link = event.target.closest(SORT_LINK_SELECTOR)
    if (!link || !this.element.contains(link)) return
    if (link.closest("turbo-frame") !== this.element) return

    this.applyWait(this.element.id)
  }

  onFrameLoad(event) {
    const loadedFrame = event.target
    if (!this.waitingForFrameId) return

    const targetMatches =
      loadedFrame === this.element && this.waitingForFrameId === this.element.id
    const nestedTargetMatches =
      loadedFrame !== this.element &&
      this.element.contains(loadedFrame) &&
      loadedFrame.tagName === "TURBO-FRAME" &&
      loadedFrame.id === this.waitingForFrameId

    if (!targetMatches && !nestedTargetMatches) return

    this.clearWait()
  }

  onFetchError(event) {
    if (event.target !== this.element) return

    this.clearWait()
  }

  onFrameMissing(event) {
    if (event.target !== this.element) return

    this.clearWait()
  }

  visibleSkeleton() {
    return Array.from(this.element.querySelectorAll(SKELETON_SELECTOR)).find(
      (node) =>
        node.closest("template") == null &&
        node.closest("turbo-frame") === this.element
    )
  }

  hasSkeleton() {
    return this.visibleSkeleton() != null
  }

  applyWait(waitingForFrameId = this.element.id) {
    this.waitingForFrameId = waitingForFrameId
    beginSkeletonWait(this.element)
    this.scheduleSafetyClear()
  }

  clearWait() {
    window.clearTimeout(this.safetyTimeout)
    this.waitingForFrameId = null
    endSkeletonWait(this.element)
  }

  scheduleSafetyClear() {
    window.clearTimeout(this.safetyTimeout)
    this.safetyTimeout = window.setTimeout(() => this.clearWait(), WAITING_SAFETY_MS)
  }

  isFetchForThisFrame(event) {
    if (event.target === this.element) return true

    return turboFrameHeader(event.detail?.fetchOptions?.headers) === this.element.id
  }
}
